import React, { useState, useEffect } from 'react';
import { Container, Button, Spinner, Alert } from 'react-bootstrap';
import { useParams, useHistory } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowLeft, faPrint, faFolder } from '@fortawesome/free-solid-svg-icons';
import { API_BASE_URL } from '../../../service/api';
import '../common.css';

// Blue button styles consistent with other components
const blueButtonStyle = {
  backgroundColor: 'rgb(77, 122, 255)',
  borderColor: 'rgb(77, 122, 255)',
  color: '#fff'
};

const blueButtonHoverStyle = {
  backgroundColor: 'rgb(60, 105, 240)',
  borderColor: 'rgb(60, 105, 240)',
  color: '#fff'
};

const ViewTourPlan = () => {
  const { planId } = useParams();
  const history = useHistory();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [htmlContent, setHtmlContent] = useState('');
  const [tourPlan, setTourPlan] = useState(null);

  useEffect(() => {
    fetchTourPlan();
  }, [planId]);

  const fetchTourPlan = async () => {
    setLoading(true);
    
    try {
      // First fetch the plan details
      const planResponse = fetch(`${API_BASE_URL}/api/tour-planner/${planId}/`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('accessToken')}`,
          'Content-Type': 'application/json'
        }
      });
      
      setTourPlan(planResponse.data);
      
      // Then fetch the HTML content
      const contentResponse = fetch(`${API_BASE_URL}/api/tour-plan-download/${planId}/`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('accessToken')}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (contentResponse.data && contentResponse.data.html) {
        setHtmlContent(contentResponse.data.html);
      } else {
        throw new Error('No HTML content received');
      }
    } catch (err) {
      console.error('Error fetching tour plan:', err);
      setError('Failed to load tour plan. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handlePrintPlan = () => {
    if (!htmlContent) {
      alert('No content available to print.');
      return;
    }
    const printWindow = window.open('', '_blank');
    if (!printWindow) {
      alert('Please allow pop-ups to print the tour plan.');
      return;
    }
    printWindow.document.write(htmlContent);
    printWindow.document.close();
    printWindow.setTimeout(() => {
      printWindow.focus();
      try {
        printWindow.print();
      } catch (e) {
        console.error("Print error:", e);
        alert("An error occurred while trying to print. Please try again.");
      }
    }, 1000);
  };

  return (
    <Container fluid className="p-0 dark-container">
      <div className="dark-header py-2 glass-bg mb-2" style={{ borderRadius: '0 0 12px 12px' }}>
        <Container>
          <div className="d-flex justify-content-between align-items-center">
            <h2 className="mb-0 h5" style={{ color: 'rgb(77, 122, 255)' }}>
              {tourPlan ? `${tourPlan.tour_name || 'Tour Plan'} - ${tourPlan.artist_name || 'Artist'}` : 'Tour Plan Details'}
            </h2>
            <div>
              <Button 
                style={blueButtonStyle}
                onMouseOver={e => e.currentTarget.style = {...blueButtonHoverStyle}}
                onMouseOut={e => e.currentTarget.style = {...blueButtonStyle}}
                size="sm" 
                className="me-2"
                onClick={() => history.push('/ai-workflows/ai-tour-planner/saved')}
              >
                <FontAwesomeIcon icon={faArrowLeft} className="me-2" />
                Back to Tour Plans
              </Button>
              <Button 
                style={blueButtonStyle}
                onMouseOver={e => e.currentTarget.style = {...blueButtonHoverStyle}}
                onMouseOut={e => e.currentTarget.style = {...blueButtonStyle}}
                size="sm"
                onClick={handlePrintPlan}
              >
                <FontAwesomeIcon icon={faPrint} className="me-2" />
                Print
              </Button>
            </div>
          </div>
        </Container>
      </div>
      
      <Container fluid className="px-0">
        {error && <Alert variant="danger" className="dark-alert m-3">{error}</Alert>}
        
        {loading ? (
          <div className="text-center my-5">
            <Spinner animation="border" variant="light" />
            <p className="text-light mt-3">Loading tour plan...</p>
          </div>
        ) : (
          <div style={{ height: 'calc(100vh - 80px)' }}>
            <iframe
              srcDoc={htmlContent}
              title={`Tour Plan - ${tourPlan?.tour_name || 'Details'}`}
              style={{ 
                width: '100%', 
                height: '100%', 
                border: 'none',
                borderRadius: '8px',
                backgroundColor: '#121212' // Dark background matching theme
              }}
            />
          </div>
        )}
      </Container>
    </Container>
  );
};

export default ViewTourPlan; 